
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Breadcrumb from './Breadcrumb';
import PageTitle from './PageTitle';
import SubNavigation from './SubNavigation';
import seoMetadata from '../../seo/seo-metadata.json';
import { updateSEO } from '../../utils/seo';
import FaqSection from '../sections/FaqSection';
import { getFaqForRoute, normalizeFaqRoute } from '../../data/faq';

const pagesWithOwnFaq = new Set(['/contact','/en/contact','/en/membership','/services/footman','/services/butler','/services/house-steward','/services/second-steward','/en/services/footman','/en/services/butler','/en/services/house-steward','/en/services/second-steward','/qualifications/business-manner','/qualifications/event-planning','/qualifications/household-manager','/qualifications/travel-concierge','/qualifications/wine-steward','/qualifications/afternoon-tea','/qualifications/butler-style-uniform','/qualifications/dining-coordinator','/qualifications/tea-sommelier','/standards/deportment','/seminars/corporate/communication-skills','/seminars/corporate/crisis-management','/seminars/corporate/schedule-management','/related','/related/the-butler','/related/butler-bell','/related/butler-academy','/en/related','/en/related/the-butler','/en/related/butler-bell','/en/related/butler-academy']);

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface SubNavItem {
  label: string;
  href: string;
  isActive?: boolean;
}

interface LayoutProps {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  pageTitle?: string;
  subNavigation?: SubNavItem[];
}

export default function Layout({ 
  children, 
  breadcrumbs, 
  pageTitle, 
  subNavigation 
}: LayoutProps) {
  const { pathname } = useLocation();
  const normalizedPath = normalizeFaqRoute(pathname);
  const faqItems = getFaqForRoute(normalizedPath);
  const showFaq = faqItems.length > 0 && !pagesWithOwnFaq.has(normalizedPath);

  useEffect(() => {
    const normalize = (path: string) => path === '/' ? '/' : path.replace(/\/+$/, '');
    const currentPath = normalize(pathname);
    const isEnglish = currentPath === '/en' || currentPath.startsWith('/en/');
    const metadataRoute = isEnglish ? normalize(currentPath.slice(3) || '/') : currentPath;
    const metadata = seoMetadata.find(entry => normalize(entry.route) === metadataRoute);

    if (!metadata) return;

    updateSEO({
      title: isEnglish ? metadata.title_en : metadata.title,
      description: isEnglish ? metadata.description_en : metadata.description,
      keywords: (isEnglish ? metadata.keywords_en : metadata.keywords).join(', '),
      canonical: currentPath,
    });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col font-['Noto_Sans_JP','Helvetica_Neue',Arial,sans-serif] leading-relaxed">
      <Header />
      
      {breadcrumbs && <Breadcrumb items={breadcrumbs} />}
      
      {pageTitle && <PageTitle title={pageTitle} />}
      
      {subNavigation && <SubNavigation items={subNavigation} />}
      
      <main className="flex-1">
        {children}
      </main>
      
      {showFaq && <FaqSection items={faqItems} isEnglish={normalizedPath === '/en' || normalizedPath.startsWith('/en/')} />}
      <Footer />
    </div>
  );
}
